'use client';

import { Open, Swap } from '@velocity-exchange/icons';
import NumberDisplay from '../../../NumberDisplay/NumberDisplay';
import InlineLoadingBar from '../../../InlineLoadingBar/InlineLoadingBar';
import TableV2 from '../../../Tables/TableV2';
import { getUrlForAccount, getUrlForTx } from 'src/utils/explorer';
import { useOrderRowData } from './useOrderRowData';
import type { OrderRowCollapsedContentProps } from './types';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import DevRecordTooltip from './DevRecordTooltip';
import { PRESETS } from 'src/utils/formatNumber/presets';
import { formatText } from '@velocity-exchange/common/format';

export default function OrderRowCollapsedContentMobile({
	orderId,
	displayDatesAsTimestamp,
	expanded,
	toggleDateDisplayType,
	amountDisplayType,
	orderedQuoteValue,
}: OrderRowCollapsedContentProps) {
	const { processedRecords, isLoading, hasData } = useOrderRowData({
		orderId,
		expanded,
	});

	const devSwitchIsOn = useDevSwitchIsOn();

	if (!hasData) {
		if (isLoading) {
			return (
				<div className="flex h-full w-full items-center justify-center py-4">
					<InlineLoadingBar />
				</div>
			);
		}
		return null;
	}

	return (
		<div className="w-full rounded-sm bg-main-bg p-md">
			{processedRecords.map((data, index) => (
				<DevRecordTooltip
					key={`${data.record.txSig}_${index}`}
					record={data.record}
					devSwitchIsOn={devSwitchIsOn}
				>
					<div
						className={
							index !== processedRecords.length - 1
								? 'mb-3 border-b border-stroke-divider pb-md'
								: undefined
						}
					>
						<div className="flex flex-col gap-3">
							<div className="flex w-full items-start justify-start">
								<div className="flex min-h-px min-w-px grow basis-0 flex-col items-start justify-center gap-y-xs">
									<MobileTableCard.Label>Avg. Fill Price</MobileTableCard.Label>
									<span className="typo-label-xs text-text-default">
										{data.isNonFillAction
											? '-'
											: data.avgPriceBn.toNotional(
													undefined,
													undefined,
													data.priceDecimalOverride
												)}
									</span>
								</div>
								<div className="flex w-[88px] flex-col items-start justify-center gap-y-xs">
									<MobileTableCard.Label>
										{amountDisplayType === 'base'
											? 'Order Size'
											: 'Order Value'}
									</MobileTableCard.Label>
									<span className="typo-label-xs text-text-default">
										{data.orderedIsEntire ? (
											'Entire Position'
										) : amountDisplayType === 'base' ? (
											<NumberDisplay
												value={data.tradeInfo.baseAssetAmount}
												options={PRESETS.tradePrecisionTrim}
												isAccountValueToHide
											/>
										) : (
											(orderedQuoteValue ?? 'Market')
										)}
									</span>
								</div>
								<div className="flex min-h-px min-w-px grow basis-0 flex-col items-end justify-center gap-y-xs">
									<MobileTableCard.Label>
										{amountDisplayType === 'base'
											? 'Filled Size'
											: 'Filled Value'}
									</MobileTableCard.Label>
									<div className="flex items-center justify-start gap-x-xs">
										<span className="typo-label-xs text-text-default">
											{data.isNonFillAction ? (
												'-'
											) : amountDisplayType === 'base' ? (
												<NumberDisplay
													value={data.tradeInfo.baseAssetAmountFilled}
													options={PRESETS.tradePrecisionTrim}
													isAccountValueToHide
												/>
											) : (
												formatText(
													data.tradeInfo.quoteAssetAmountFilled,
													PRESETS.usd
												)
											)}
										</span>
									</div>
								</div>
							</div>
							<div className="flex w-full items-start justify-start">
								<div className="flex min-h-px min-w-px grow basis-0 flex-col items-start justify-center gap-y-xs">
									<MobileTableCard.Label>
										{amountDisplayType === 'base'
											? 'Cumulative Size'
											: 'Cumulative Value'}
									</MobileTableCard.Label>
									<span className="typo-label-xs text-text-default">
										{data.isNonFillAction ? (
											'-'
										) : amountDisplayType === 'base' ? (
											<NumberDisplay
												value={data.tradeInfo.totalBaseAmountFilled}
												options={PRESETS.tradePrecisionTrim}
												isAccountValueToHide
											/>
										) : (
											formatText(
												data.tradeInfo.totalQuoteAmountFilled,
												PRESETS.usd
											)
										)}
									</span>
								</div>
								<div className="flex w-[88px] flex-col items-start justify-center gap-y-xs">
									<MobileTableCard.Label>Fee</MobileTableCard.Label>
									<span className="typo-label-xs text-text-default">
										{data.isNonFillAction || !data.tradeInfo.fee ? (
											'-'
										) : (
											<NumberDisplay
												value={data.tradeInfo.fee}
												options={PRESETS.usd}
											/>
										)}
									</span>
								</div>
								<div className="flex min-h-px min-w-px grow basis-0 flex-col items-end justify-center gap-y-xs">
									<MobileTableCard.Label>Filled By</MobileTableCard.Label>
									<span className="flex items-center gap-x-xs typo-label-xs text-text-default">
										{data.isNonFillAction ? (
											'-'
										) : data.isDefaultCounterparty ? (
											data.filledBy
										) : (
											<>
												{data.filledBy}
												<a
													className="flex"
													href={
														data.counterpartyStr
															? getUrlForAccount(data.counterpartyStr)
															: '#'
													}
													target="_blank"
													rel="noopener noreferrer"
													onClick={(e) => e.stopPropagation()}
												>
													<Open autoSize />
												</a>
											</>
										)}
									</span>
								</div>
							</div>
							<div className="flex w-full items-start justify-start">
								<div className="flex min-h-px min-w-px grow basis-0 flex-col items-start justify-start gap-y-xs">
									<MobileTableCard.Label>Status</MobileTableCard.Label>
									<div className="flex items-center justify-start gap-x-xs">
										<span className="typo-label-xs text-text-default">
											{data.status === 'Partially Filled (Cancelled)'
												? 'Cancelled'
												: data.status}
										</span>
										{data.record.txSig && (
											<a
												className="flex"
												href={getUrlForTx(data.record.txSig)}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
											>
												<Open autoSize />
											</a>
										)}
									</div>
								</div>
								<div className="flex min-h-px min-w-px grow basis-0 flex-col items-end justify-center gap-y-xs">
									<div className="flex items-center justify-start gap-x-xs">
										<MobileTableCard.Label>Time</MobileTableCard.Label>
										<IconButton
											size="sm"
											variant="tertiary"
											onClick={toggleDateDisplayType}
											className="text-text-tertiary"
											icon={Swap}
										/>
									</div>
									<TableV2.DateCell
										className="justify-end py-0 typo-label-xs whitespace-nowrap text-text-default"
										displayAsTimestamp={displayDatesAsTimestamp}
										date={data.date}
									/>
								</div>
							</div>
						</div>
					</div>
				</DevRecordTooltip>
			))}
		</div>
	);
}
